import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Trash2, Eye, Download, History, FileText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/constants";
import { RecordStatus, fileBlobUrl } from "@/lib/records";

function FileItem({ recordId, f }) {
  const [url, setUrl] = useState(null);
  useEffect(() => () => url && URL.revokeObjectURL(url), [url]);
  const open = () => fileBlobUrl(recordId, f.file_id).then(setUrl).catch((e) => toast.error(formatApiError(e)));
  const save = () => fileBlobUrl(recordId, f.file_id).then((u) => { const a = document.createElement("a"); a.href = u; a.download = f.name; a.click(); }).catch((e) => toast.error(formatApiError(e)));
  const canPreview = /pdf|jpeg|png/.test(f.content_type);
  return (
    <div className="rounded-xl border border-lasso-line p-3" data-testid={`record-file-${f.file_id}`}>
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="flex items-center gap-2 truncate"><FileText className="h-4 w-4 shrink-0 text-lasso-deep" />{f.name}<span className="text-xs text-zinc-400">{Math.ceil(f.size / 1024)} KB</span></span>
        <span className="flex gap-3 text-xs font-semibold">
          {canPreview && !url && <button onClick={open} className="inline-flex items-center gap-1 text-lasso-deep" data-testid={`record-file-preview-${f.file_id}`}><Eye className="h-3.5 w-3.5" /> Preview</button>}
          <button onClick={save} className="inline-flex items-center gap-1 text-lasso-deep" data-testid={`record-file-download-${f.file_id}`}><Download className="h-3.5 w-3.5" /> Download</button>
        </span>
      </div>
      {url && (f.content_type === "application/pdf"
        ? <iframe title={f.name} src={url} className="mt-3 h-[60vh] w-full rounded-lg" data-testid={`record-file-frame-${f.file_id}`} />
        : <img src={url} alt={f.name} className="mt-3 max-h-[60vh] rounded-lg" data-testid={`record-file-img-${f.file_id}`} />)}
    </div>
  );
}

export default function FormRecordDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [summary, setSummary] = useState(state?.summary || null);
  const [r, setR] = useState(null);
  const [err, setErr] = useState(null);
  const load = useCallback(() => api.get(`/form-records/${id}`).then((x) => setR(x.data)).catch((e) => setErr(formatApiError(e))), [id]);
  useEffect(() => { load(); }, [load]);

  const remove = async () => {
    if (!window.confirm("Delete this record? It stays in the audit history and can be viewed by admins with “Show deleted”.")) return;
    try { await api.delete(`/form-records/${id}`); toast.success("Record deleted"); navigate("/form-records"); } catch (e) { toast.error(formatApiError(e)); }
  };
  if (err) return <p className="text-sm text-rose-600" data-testid="record-error">{err}</p>;
  if (!r) return null;
  const req = new Set(r.required_form_ids);
  return (
    <div className="fade-up">
      <Link to="/form-records" className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-zinc-500 hover:text-lasso-ink" data-testid="record-back-link"><ArrowLeft className="h-4 w-4" /> Form records</Link>
      <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="flex items-center gap-2"><RecordStatus status={r.status} missing={r.missing} testId="record-status" />{r.deleted && <span className="rounded-full bg-rose-50 px-2.5 py-0.5 text-[11px] font-semibold text-rose-700" data-testid="record-deleted-badge">Deleted</span>}</div>
          <h1 className="mt-3 font-display text-3xl font-bold" data-testid="record-title">{r.event_type_name} · {r.resident_name}</h1>
          <p className="mt-2 text-sm text-zinc-500">{r.community_name} · <Link to={`/form-records?lot=${encodeURIComponent(r.lot)}&community_id=${r.community_id}`} className="font-semibold text-lasso-deep hover:underline" data-testid="record-lot-link">Lot {r.lot}</Link> · event {r.event_date} · logged by {r.created_by}</p>
          {r.missing.length > 0 && <p className="mt-3 rounded-xl bg-amber-50 px-4 py-2 text-sm text-amber-900" data-testid="record-missing-list">Missing: {r.missing.join(", ")}</p>}
        </div>
        {!r.deleted && (
          <div className="flex gap-2">
            {r.can_edit && <Link to={`/form-records/${id}/edit`} className="inline-flex items-center gap-2 rounded-full bg-lasso-ink px-4 py-2.5 text-sm font-semibold text-white hover:bg-lasso-navy" data-testid="record-edit-button"><Pencil className="h-4 w-4" /> {r.missing.length ? "Add missing forms" : "Edit"}</Link>}
            {user.role === "admin" && <button onClick={remove} className="inline-flex items-center gap-2 rounded-full border border-rose-200 px-4 py-2.5 text-sm font-semibold text-rose-600 hover:bg-rose-50" data-testid="record-delete-button"><Trash2 className="h-4 w-4" /> Delete</button>}
          </div>
        )}
      </div>
      {summary && (
        <div className="mt-6 rounded-2xl border border-lasso-line bg-white p-5" data-testid="record-save-summary">
          <div className="flex items-center justify-between"><p className="text-sm font-semibold">Saved. Files now on this record:</p><button onClick={() => setSummary(null)} className="text-zinc-400" data-testid="record-save-summary-close">×</button></div>
          <ul className="mt-2 space-y-1 text-sm">
            {summary.map((x) => (
              <li key={x.form} className={x.files ? (x.files < x.expected ? "text-rose-600" : "text-emerald-700") : "text-amber-800"} data-testid="record-save-summary-item">
                {x.form}: {x.files ? `${x.files} file${x.files > 1 ? "s" : ""}` : "no file"}{x.files < x.expected ? ` (expected ${x.expected})` : ""}
              </li>
            ))}
            {(state?.missing || []).map((m) => <li key={m} className="text-amber-800" data-testid="record-save-summary-missing">{m}: not logged</li>)}
          </ul>
        </div>
      )}
      <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_320px]">
        <div className="space-y-4" data-testid="record-entries-list">
          {r.entries.map((e) => (
            <div key={e.form_id} className="rounded-2xl border border-lasso-line bg-white p-5" data-testid={`record-entry-${e.form_id}`}>
              <p className="font-semibold">{e.form_title} <span className="ml-1 text-[10px] font-bold uppercase tracking-wider text-zinc-400">{req.has(e.form_id) ? "Required" : "Optional"}</span></p>
              <p className="mt-1 text-xs text-zinc-500">Completed {e.completed_date || "—"} by {e.staff_name || "—"}{e.notes ? ` · ${e.notes}` : ""}</p>
              <div className="mt-3 space-y-2">{e.files.length ? e.files.map((f) => <FileItem key={f.file_id} recordId={id} f={f} />) : <p className="text-xs text-amber-700">No upload attached.</p>}</div>
            </div>
          ))}
          {!r.entries.length && <p className="text-sm text-zinc-500">No forms logged yet.</p>}
        </div>
        <aside data-testid="record-history">
          <p className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-zinc-400"><History className="h-4 w-4" /> Audit history</p>
          <ol className="space-y-4 border-l border-lasso-line pl-4">
            {r.history.slice().reverse().map((hh, i) => (
              <li key={i} className="text-sm" data-testid={`record-history-${i}`}>
                <p className="font-semibold capitalize">{hh.action} <span className="font-normal text-zinc-500">by {hh.by}</span></p>
                <p className="text-xs text-zinc-400">{formatDate(hh.at)} {new Date(hh.at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</p>
                <ul className="mt-1 list-disc pl-4 text-xs text-zinc-600">{hh.changes.map((c, j) => <li key={j}>{c}</li>)}</ul>
              </li>
            ))}
          </ol>
        </aside>
      </div>
    </div>
  );
}
